// @flow
import type { ChildrenArray, Element } from 'react';
import React from 'react';
import {
  types as t,
  usePropsValidation,
} from '../../validation/runtime-checks/index.js';
import type {
  SharedStatementChildren,
  SolutionChildren,
} from '../common-children-types.js';
import {
  sharedStatementChildrenChecker,
  solutionChildrenChecker,
} from '../common-children-types.js';
import {
  productColorSunflower,
  productColor,
  illustrationColor,
} from '../../designSystem.js';
import { Language, English, Spanish } from '../../languages.js';
import type { CompilerInjectedProps } from '../../validation/runtime-checks/index.js';
import { WistiaPopover } from '../../InternalWistiaModal.js';
import { InternalBunnyPopover } from '../../InternalBunnyVideo.js';
import { usePrintMode } from '../../jsx-activity/usePrintMode.js';
import { RSLMultipleChoiceImageOptions } from '../common-components/RSLMultipleChoiceImageOptions.js';
import { RSLMultipleChoiceTextOptions } from '../common-components/RSLMultipleChoiceTextOptions.js';
import { useValidateBunnyVideoInLibrary } from '../../useValidateBunnyVideoInLibrary.js';

export function RSLWorkedQuestion(props: {
  _index: number,
  walkthroughVideoId?: string, // Eg. gk9w45pd5q
  children: ChildrenArray<
    | Element<typeof RSLQuestionStatement>
    | Element<typeof RSLWorkedQuestionPart>
    | Element<typeof RSLCreateAStrategy>
    | Element<typeof RSLApplyTheIdea>
    | Element<typeof RSLReflectAndCheck>,
  >,
  ...CompilerInjectedProps,
}) {
  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    RSLWorkedQuestion,
  );
  const { _index, walkthroughVideoId, children } = props;
  const [questionStatement, ...rest] = React.Children.toArray(children);
  const isMultiPart = rest[0]?.type === RSLWorkedQuestionPart;

  useValidateBunnyVideoInLibrary({
    performValidation: props._performValidation,
    failedValidation,
    addErrors,
    location: locations.walkthroughVideoId,
    videoId: walkthroughVideoId,
  });

  useValidateWalkthroughVideoIdAbsenceForMultiPartQuestion({
    performValidation: props._performValidation,
    failedValidation,
    addErrors,
    location: locations.walkthroughVideoId,
    isMultiPart,
    walkthroughVideoId,
  });

  if (failedValidation) return null;

  return (
    <div style={{ color: productColorSunflower.grey }}>
      <div
        style={{
          breakInside: 'avoid',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <h4 style={{ position: 'relative', margin: 0 }}>
          <span
            style={{
              display: 'inline-block',
              fontFamily: 'Proxima Nova',
              fontWeight: 600,
              color: productColor.white,
              fontSize: 16,
              lineHeight: '22px',
              padding: '4px 12px',
              borderRadius: 15, // (22px + 4px) / 2
              backgroundColor: illustrationColor.niagra,
            }}
          >
            <Language>
              <English>Example {_index + 1}</English>
              <Spanish>Ejemplo {_index + 1}</Spanish>
            </Language>
          </span>
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: '50%',
              zIndex: -1,
              width: '100%',
              height: 2,
              background: illustrationColor.niagra,
            }}
          />
        </h4>
        <div style={{ height: 16 }} />
        {questionStatement}
      </div>
      {isMultiPart ? (
        // $FlowIssue cloneElement signature seems dodgy
        rest.map((child, i) => React.cloneElement(child, { _index: i }))
      ) : (
        <>
          <ExampleCollapsibleInternal>
            {/* Flow can't tell that at this point `rest` has the right type */}
            {(rest: ChildrenArray<React$Element<any>>)}
          </ExampleCollapsibleInternal>
          <InternalVideoWalkthrough videoId={walkthroughVideoId} />
        </>
      )}
    </div>
  );
}
RSLWorkedQuestion.spec = {
  // What we actually want is that the entire props interface is a union
  // type, where you can only provide a walkthroughVideoId if you are not
  // a multi-part question
  walkthroughVideoId: t.optional(t.string),
  children: t.unionOfTypes(
    t.prefixChildrenArray(
      t.elementType(RSLQuestionStatement),
      t.minimumCount(2, t.elementType(RSLWorkedQuestionPart)),
    ),
    t.exactOrderChildrenArray(
      t.elementType(RSLQuestionStatement),
      t.optional(t.elementType(RSLCreateAStrategy)),
      t.elementType(RSLApplyTheIdea),
      t.optional(t.elementType(RSLReflectAndCheck)),
    ),
  ),
};

// Our bunny video ids are in standard 8-4-4-12 UUID format, whereas Wista ids
// looks like this: gk9w45pd5q. We'll use this format distinction to determine
// which kind of video id is being provided, since we don't want to force authors
// to specify the video source. Could be an issue if we try to move from bunny
// to another service with the same id structure in the future though...
function isBunnyVideoId(value: string): boolean {
  const uuidRegex =
    /^([a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[a-f0-9]{4}-[a-f0-9]{12})$/i;
  return uuidRegex.test(value);
}

function useValidateWalkthroughVideoIdAbsenceForMultiPartQuestion({
  performValidation,
  failedValidation,
  addErrors,
  location,
  isMultiPart,
  walkthroughVideoId,
}) {
  React.useEffect(() => {
    if (!performValidation) return;
    if (failedValidation) return;

    if (isMultiPart && walkthroughVideoId !== undefined) {
      addErrors([
        {
          message:
            'multi-part questions must specify walkthroughVideoId on particular RSLWorkedQuestionPart components',
          ...location,
        },
      ]);
    }
  });
}

function InternalVideoWalkthrough({ videoId }: {| videoId: string | void |}) {
  const [openVideoId, setOpenVideoId] = React.useState<string | null>(null);
  const inPrintMode = usePrintMode();
  const noVideo = videoId === undefined;

  // We will not show the button at all if we are in print mode,
  // or if there is no videoId provided
  if (inPrintMode || noVideo) return null;

  const useBunny = videoId === undefined || isBunnyVideoId(videoId);

  return (
    <>
      <button
        style={{
          background: 'transparent',
          boxSizing: 'border-box',
          height: 40,
          borderRadius: 32,
          padding: '12px 24px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          border: `1px solid ${productColorSunflower.ironDark}`,
          gap: 10,
          color: productColorSunflower.eggplant,
          fontFamily: 'Proxima Nova',
          fontSize: 16,
          fontWeight: 600,
          marginTop: 16,
          cursor: 'pointer',
        }}
        onClick={() => {
          if (videoId === undefined) return;
          setOpenVideoId((openVideoId) =>
            openVideoId === null ? videoId : null,
          );
        }}
      >
        {openVideoId === null ? (
          <>
            <PlayIcon />
            <div>
              <Language>
                <English>Watch question walkthrough</English>
                {/* Translation isn't totally equivalent... */}
                <Spanish>Ver el video</Spanish>
              </Language>
            </div>
          </>
        ) : (
          <>
            <CloseIcon />
            <div>
              <Language>
                <English>Close question walkthrough</English>
                {/* Translation isn't totally equivalent... */}
                <Spanish>Cierra el video</Spanish>
              </Language>
            </div>
          </>
        )}
      </button>

      {openVideoId !== null &&
        (useBunny ? (
          <InternalBunnyPopover
            videoId={openVideoId}
            onClose={() => setOpenVideoId(null)}
          />
        ) : (
          // TODO: We began migrating away from Wistia to Bunny in July 2023
          // Once we have purged all Wistia videos from our content (both in
          // our repo, and in published documents in Mathspace DB) this code
          // path can be removed.
          <WistiaPopover
            videoId={openVideoId}
            onClose={() => setOpenVideoId(null)}
          />
        ))}
    </>
  );
}

function PlayIcon() {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="currentColor"
      style={{ display: 'block' }}
    >
      <path d="M5.4086 2.38352L11.6014 7.31585C12.0152 7.64542 12.1245 8.30893 11.8456 8.79786C11.7806 8.91169 11.6977 9.00966 11.6014 9.08639L5.4086 14.0187C4.99481 14.3483 4.43325 14.2191 4.15432 13.7302C4.05374 13.5539 4 13.3461 4 13.1334V3.26879C4 2.67916 4.40454 2.20117 4.90356 2.20117C5.08352 2.20117 5.25938 2.26467 5.4086 2.38352Z" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="currentColor"
      style={{ display: 'block' }}
    >
      <path d="M10.476 8.00358L15.4806 3.01243C15.6443 2.84857 15.7742 2.65403 15.8628 2.43994C15.9514 2.22584 15.997 1.99637 15.997 1.76464C15.997 1.5329 15.9514 1.30344 15.8628 1.08934C15.7742 0.875246 15.6443 0.680712 15.4806 0.51685C15.3169 0.352988 15.1225 0.223006 14.9086 0.134325C14.6946 0.0456435 14.4654 0 14.2338 0C14.0023 0 13.773 0.0456435 13.5591 0.134325C13.3452 0.223006 13.1508 0.352988 12.9871 0.51685L8 5.52558L3.01295 0.51685C2.84922 0.352988 2.65485 0.223006 2.44093 0.134325C2.22701 0.0456435 1.99773 0 1.76619 0C1.53464 0 1.30536 0.0456435 1.09144 0.134325C0.877524 0.223006 0.68315 0.352988 0.519423 0.51685C0.355696 0.680712 0.225821 0.875246 0.137212 1.08934C0.0486036 1.30344 0.00299759 1.5329 0.00299759 1.76464C0.0029976 1.99637 0.0486036 2.22584 0.137212 2.43994C0.225821 2.65403 0.355696 2.84857 0.519423 3.01243L5.52403 8.00358L0.519423 12.9947C0.354835 13.1581 0.2242 13.3525 0.13505 13.5666C0.0458997 13.7808 0 14.0105 0 14.2425C0 14.4745 0.0458997 14.7042 0.13505 14.9184C0.2242 15.1326 0.354835 15.3269 0.519423 15.4903C0.683504 15.6532 0.878098 15.7821 1.09204 15.8695C1.30599 15.957 1.53508 16.0013 1.76619 16C1.99729 16.0013 2.22638 15.957 2.44033 15.8695C2.65427 15.7821 2.84887 15.6532 3.01295 15.4903L8 10.4816L12.9871 15.4903C13.1511 15.6532 13.3457 15.7821 13.5597 15.8695C13.7736 15.957 14.0027 16.0013 14.2338 16C14.4649 16.0013 14.694 15.957 14.908 15.8695C15.1219 15.7821 15.3165 15.6532 15.4806 15.4903C15.6452 15.3269 15.7758 15.1326 15.865 14.9184C15.9541 14.7042 16 14.4745 16 14.2425C16 14.0105 15.9541 13.7808 15.865 13.5666C15.7758 13.3525 15.6452 13.1581 15.4806 12.9947L10.476 8.00358Z" />
    </svg>
  );
}

export function RSLWorkedQuestionPart(props: {|
  children: ChildrenArray<
    | Element<typeof RSLSubquestionStatement>
    | Element<typeof RSLCreateAStrategy>
    | Element<typeof RSLApplyTheIdea>
    | Element<typeof RSLReflectAndCheck>,
  >,
  _index: number,
  moveToNextPrintedPage?: boolean,
  walkthroughVideoId?: string,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    RSLWorkedQuestionPart,
  );
  const [subproblemStatement, ...rest] = React.Children.toArray(props.children);

  useValidateBunnyVideoInLibrary({
    performValidation: props._performValidation,
    failedValidation,
    addErrors,
    location: locations.walkthroughVideoId,
    videoId: props.walkthroughVideoId,
  });

  if (failedValidation) return null;

  return (
    <div
      style={{
        marginTop: props._index === 0 ? 16 : 48,
        pageBreakBefore: props.moveToNextPrintedPage ? 'always' : 'auto',
      }}
    >
      <div>
        {/* $FlowIssue cloneElement signature seems dodgy */}
        {React.cloneElement(subproblemStatement, { _index: props._index })}
      </div>
      <ExampleCollapsibleInternal>
        {/* Flow can't tell that at this point `rest` has the right type */}
        {(rest: ChildrenArray<React$Element<any>>)}
      </ExampleCollapsibleInternal>
      <InternalVideoWalkthrough videoId={props.walkthroughVideoId} />
    </div>
  );
}
RSLWorkedQuestionPart.spec = {
  children: t.exactOrderChildrenArray(
    t.elementType(RSLSubquestionStatement),
    t.optional(t.elementType(RSLCreateAStrategy)),
    t.elementType(RSLApplyTheIdea),
    t.optional(t.elementType(RSLReflectAndCheck)),
  ),
  moveToNextPrintedPage: t.optional(t.boolean),
  walkthroughVideoId: t.optional(t.string),
};

export function RSLQuestionStatement(props: {
  children: ChildrenArray<SharedStatementChildren>,
  ...CompilerInjectedProps,
}) {
  const { failedValidation, addErrors } = usePropsValidation(
    props,
    RSLQuestionStatement,
  );

  // Custom validation to ensure at most one multiple choice component is used
  // (RSLMultipleChoiceTextOptions or RSLMultipleChoiceImageOptions)
  React.useEffect(() => {
    if (!props._performValidation) return;
    if (failedValidation) return;

    const multiChoiceElements: Array<
      | React$Element<typeof RSLMultipleChoiceTextOptions>
      | React$Element<typeof RSLMultipleChoiceImageOptions>,
    > = (React.Children.toArray(props.children).filter(
      (child) =>
        child.type === RSLMultipleChoiceTextOptions ||
        child.type === RSLMultipleChoiceImageOptions,
    ): any);

    const errors = [];
    for (const element of multiChoiceElements.slice(1)) {
      errors.push({
        message: `Only 1 multiple choice component can be used in RSLQuestionStatement`,
        ...element.props._sourceLocations?.[element.type.name ?? ''],
      });
    }
    if (errors.length > 0) addErrors(errors);
  });

  if (failedValidation) return null;
  return <div style={{ breakInside: 'avoid' }}>{props.children}</div>;
}
RSLQuestionStatement.spec = {
  children: sharedStatementChildrenChecker,
};

export function RSLSubquestionStatement(props: {|
  children: ChildrenArray<SharedStatementChildren>,
  _index: number,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation, addErrors } = usePropsValidation(
    props,
    RSLSubquestionStatement,
  );

  // Custom validation to ensure at most one multiple choice component is used
  // (RSLMultipleChoiceTextOptions or RSLMultipleChoiceImageOptions)
  React.useEffect(() => {
    if (!props._performValidation) return;
    if (failedValidation) return;

    const multiChoiceElements: Array<
      | React$Element<typeof RSLMultipleChoiceTextOptions>
      | React$Element<typeof RSLMultipleChoiceImageOptions>,
    > = (React.Children.toArray(props.children).filter(
      (child) =>
        child.type === RSLMultipleChoiceTextOptions ||
        child.type === RSLMultipleChoiceImageOptions,
    ): any);

    const errors = [];
    for (const element of multiChoiceElements.slice(1)) {
      errors.push({
        message: `Only 1 multiple choice component can be used in RSLSubquestionStatement`,
        ...element.props._sourceLocations?.[element.type.name ?? ''],
      });
    }
    if (errors.length > 0) addErrors(errors);
  });

  if (failedValidation) return null;

  return (
    <div style={{ display: 'flex', breakInside: 'avoid' }}>
      <div
        style={{
          color: productColorSunflower.grey90,
          borderRadius: 4,
          fontWeight: 600,
          fontSize: 16,
        }}
      >
        {String.fromCharCode(97 + props._index)}
      </div>
      <div style={{ width: 8, flexShrink: 0 }} />
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {props.children}
      </div>
    </div>
  );
}

RSLSubquestionStatement.spec = {
  children: sharedStatementChildrenChecker,
};

export function RSLCreateAStrategy(props: {
  children: ChildrenArray<SolutionChildren>,
}) {
  const { failedValidation } = usePropsValidation(props, RSLCreateAStrategy);
  if (failedValidation) return null;
  return (
    <InternalExampleSection
      label={
        <Language>
          <English>Create a strategy</English>
          <Spanish>Crea una estrategia</Spanish>
        </Language>
      }
    >
      {props.children}
    </InternalExampleSection>
  );
}
RSLCreateAStrategy.spec = { children: solutionChildrenChecker };

export function RSLApplyTheIdea(props: {
  children: ChildrenArray<SolutionChildren>,
}) {
  const { failedValidation } = usePropsValidation(props, RSLApplyTheIdea);
  if (failedValidation) return null;
  return (
    <InternalExampleSection
      label={
        <Language>
          <English>Apply the idea</English>
          <Spanish>Aplica la idea</Spanish>
        </Language>
      }
    >
      {props.children}
    </InternalExampleSection>
  );
}
RSLApplyTheIdea.spec = { children: solutionChildrenChecker };

export function RSLReflectAndCheck(props: {
  children: ChildrenArray<SolutionChildren>,
}) {
  const { failedValidation } = usePropsValidation(props, RSLReflectAndCheck);
  if (failedValidation) return null;
  return (
    <InternalExampleSection
      label={
        <Language>
          <English>Reflect and check</English>
          <Spanish>Reflexiona y verifica</Spanish>
        </Language>
      }
    >
      {props.children}
    </InternalExampleSection>
  );
}
RSLReflectAndCheck.spec = { children: solutionChildrenChecker };

function InternalExampleSection({
  label,
  children,
}: {|
  label: Element<typeof Language>,
  children: React$Node,
|}) {
  return (
    <div style={{ breakInside: 'avoid' }}>
      <div
        style={{
          // TODO once again NOT a sunflower, nor student color
          color: '#0875BE',
          fontSize: 16,
          fontWeight: 600,
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function ExampleSectionDivider() {
  // TODO sunflow/progress color tropicalBlue
  return <div style={{ height: 1, margin: '24px 0', background: '#D5E8F9' }} />;
}

function interleave<T: React$Element<*>>(e: T, es: T[]) {
  if (es.length === 0) return [];
  const result = [es.shift()];
  while (es[0] !== undefined) {
    result.push(e);
    result.push(es.shift());
  }
  // NOTE poorly chosen numeric keys at the call site can cause issues here...
  const withKeys = result.map<T>((e, i) =>
    React.cloneElement(e, { key: e.key || i }),
  );
  return withKeys;
}

function ExampleCollapsibleInternal({
  children,
  openByDefault,
}: {
  children: ChildrenArray<
    | Element<typeof RSLCreateAStrategy>
    | Element<typeof RSLApplyTheIdea>
    | Element<typeof RSLReflectAndCheck>,
  >,
  openByDefault?: boolean,
}) {
  const inPrintMode = usePrintMode();
  const [open, setOpen] = React.useState(openByDefault || inPrintMode);
  return (
    <div
      style={{
        marginTop: 20,
        // TODO this is NOT a sunflower color, its a teacher-only
        // color from classic mathspace
        backgroundColor: '#F3F9FF',
        borderRadius: 8,
        padding: '24px 16px',
      }}
    >
      <div
        style={{
          textAlign: 'end',
          height: 69,
          margin: '-24px -16px -45px',
          paddingRight: 24,
          cursor: 'pointer',
          position: 'relative',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
        }}
        onClick={() => setOpen((prevState) => !prevState)}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          style={{
            transform: open ? 'rotate(180deg)' : undefined,
          }}
        >
          <path
            d="M0.159817 4.36855L7.6314 11.8396C7.73815 11.9465 7.86096 12 7.99989 12C8.13881 12 8.26185 11.9465 8.36865 11.8396L15.8396 4.36855C15.9467 4.26151 16 4.13871 16 3.99972C16 3.8608 15.9465 3.73782 15.8396 3.63101L15.0382 2.82963C14.9313 2.72277 14.8083 2.66937 14.6694 2.66937C14.5305 2.66937 14.4075 2.72277 14.3007 2.82963L7.99989 9.13028L1.6989 2.82924C1.59209 2.72237 1.46906 2.66931 1.33036 2.66931C1.19121 2.66931 1.06823 2.72271 0.961422 2.82924L0.159873 3.63096C0.0530672 3.73776 0 3.8608 0 3.99967C-5.53131e-05 4.1387 0.0530109 4.26168 0.159817 4.36855Z"
            fill={productColor.lochmara}
          />
        </svg>
      </div>
      <div
        style={{
          color: '#0875BE',
          fontSize: 16,
          fontWeight: 600,
          // When we are closed we still want children to be in the DOM
          // AND (critically) we want the children to exist in the box tree
          // as well, so that we can perform accurate layout measurements.
          // This combination of styles ensures children exist in the box
          // tree, but do not affect layout, nor are drawn to the screen.
          lineHeight: '21px',
          visibility: open ? 'hidden' : 'visible',
          height: open ? 0 : 'auto',
        }}
      >
        Worked Solution
      </div>
      <div
        style={{
          // When we are closed we still want children to be in the DOM
          // AND (critically) we want the children to exist in the box tree
          // as well, so that we can perform accurate layout measurements.
          // This combination of styles ensures children exist in the box
          // tree, but do not affect layout, nor are drawn to the screen.
          visibility: open ? 'visible' : 'hidden',
          height: open ? 'auto' : 0,
        }}
      >
        {interleave(
          <ExampleSectionDivider />,
          React.Children.toArray(children),
        )}
      </div>
    </div>
  );
}
